import { SelectField, TextField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import {
  Accordion,
  Button,
  Card,
  Col,
  FormGroup,
  Icon,
  Row,
  Text,
  Title,
  useConfiguredForm,
} from '@wyz/ui';
import { useSet } from '@wyz/utils';
import clsx from 'clsx';
import { get } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useManageCustomers } from '~/common/api';
import { ENTITY_CONTACT_TYPE_LOOKUP } from '~/connect/helpers';
import type { ICustomerContactForm } from '~/connect/types';

function getContactTitle(
  firstName: string,
  lastName: string,
  email: string,
): string {
  let title = '';
  if (firstName.length > 0 || lastName.length > 0) {
    title = `${firstName} ${lastName}`;
  } else if (email.length > 0) {
    title = email;
  }
  return title;
}

interface ContactItemProps {
  contactId: string;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  onRemove: () => void;
  contacts: ICustomerContactForm['contacts'];
}

const ContactItem = ({
  contactId,
  index,
  isOpen,
  onToggle,
  onRemove,
}: ContactItemProps) => {
  const { t } = useI18n();
  const methods = useFormContext<ICustomerContactForm>();
  const configurationContext = useConfiguredForm();
  const manageCustomers = useManageCustomers({ getTypes: { enabled: true } });

  const hasError = !!get(methods.formState.errors, `contacts.${index}`);
  const firstName = useWatch({
    name: `contacts.${index}.firstName`,
    control: methods.control,
  });
  const lastName = useWatch({
    name: `contacts.${index}.lastName`,
    control: methods.control,
  });
  const email = useWatch({
    name: `contacts.${index}.email`,
    control: methods.control,
  });

  return (
    <Accordion.Container
      key={contactId}
      activeKey={isOpen ? [contactId] : []}
      className={clsx('border border-x', {
        'border-danger': hasError,
      })}
      flush
    >
      <Accordion.Item eventKey={contactId} className='position-relative'>
        <Accordion.Header onClick={onToggle}>
          <Text heading='normal' weight='normal'>
            {getContactTitle(firstName, lastName, email)}
          </Text>
        </Accordion.Header>
        <Accordion.Body>
          {configurationContext.mode === 'WRITE' && (
            <div
              className='position-absolute'
              style={{ right: '5%', top: '6%', zIndex: 10 }}
            >
              <Button
                id={`remove-contact-${index + 1}`}
                variant='tertiary-grey'
                size='sm'
                type='button'
                onClick={onRemove}
              >
                <Icon icon='trash' width={15} height={15} />
                <span className='ms-2'>
                  <Trans i18nKey='app.common.buttons.delete' />
                </span>
              </Button>
            </div>
          )}
          <Row>
            <Col sm={4}>
              <TextField
                name={`contacts.${index}.firstName`}
                label={<Trans i18nKey='app.entity.form.contact.first_name' />}
              />
            </Col>
            <Col sm={4}>
              <TextField
                name={`contacts.${index}.lastName`}
                label={<Trans i18nKey='app.entity.form.contact.last_name' />}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3}>
              <SelectField
                name={`contacts.${index}.types`}
                label={<Trans i18nKey='app.common.form.contact_type' />}
                options={
                  manageCustomers.getTypes.data?.data?.authorizedTypes.map(
                    (type) => {
                      return {
                        label: t(ENTITY_CONTACT_TYPE_LOOKUP[type]),
                        value: type,
                      };
                    },
                  ) ?? []
                }
                tooltips={
                  <Trans i18nKey='app.entity.form.contact.types_tooltip' />
                }
                isMulti
                isLoading={manageCustomers.getTypes.isLoading}
              />
            </Col>
            <Col sm={3}>
              <TextField
                name={`contacts.${index}.email`}
                label={<Trans i18nKey='app.common.email' />}
                required
              />
            </Col>
            <Col sm={3}>
              <TextField
                name={`contacts.${index}.phone`}
                label={<Trans i18nKey='app.entity.form.contact.phone' />}
              />
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion.Container>
  );
};

const ContactForm = () => {
  const methods = useFormContext<ICustomerContactForm>();

  const {
    fields: contacts,
    append,
    remove,
  } = useFieldArray<ICustomerContactForm, 'contacts'>({
    control: methods.control,
    name: 'contacts',
  });

  const [openContacts, openContactsHandlers] = useSet<string>();
  // open newly added contact
  const oldContactsLength = useRef(contacts.length);
  useEffect(() => {
    if (contacts.length > oldContactsLength.current) {
      openContactsHandlers.add(contacts[contacts.length - 1].id);
    }
    oldContactsLength.current = contacts.length;
  }, [contacts, contacts.length, openContactsHandlers]);

  const watchedContacts = useWatch({
    name: 'contacts',
    control: methods.control,
  });
  const configurationContext = useConfiguredForm();

  return (
    <Card>
      <Card.Header>
        <Title heading='lg' color='tertiary' weight='bold'>
          <Trans i18nKey='app.entity.form.contact' />
        </Title>
      </Card.Header>
      <Card.Body>
        <>
          <FormGroup className='space-y-2'>
            {contacts.map((contact, index) => (
              <ContactItem
                key={contact.id}
                contactId={contact.id}
                index={index}
                contacts={watchedContacts}
                isOpen={openContacts.includes(contact.id)}
                onToggle={() => openContactsHandlers.toggle(contact.id)}
                onRemove={() => {
                  remove(index);
                  openContactsHandlers.remove(contact.id);
                }}
              />
            ))}
          </FormGroup>
          {configurationContext.mode === 'WRITE' && (
            <Button
              id='add-contact'
              variant='tertiary-blue'
              size='sm'
              type='button'
              onClick={() => {
                append({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  types: [],
                  default: false,
                });
              }}
            >
              <Icon icon='plus' width={15} height={15} />
              <span className='ms-2'>
                <Trans i18nKey='app.entity.form.action.add_contact' />
              </span>
            </Button>
          )}
        </>
      </Card.Body>
    </Card>
  );
};

export default ContactForm;
